// Libraries
import { useMediaQuery } from 'react-responsive';

// Hooks
import { useAppSelector, useAppDispatch } from '../../../app/hooks';

// Features
import {
    pushPlayersListStatus,
    selectPlayersListStatus,
} from '../../../features/app/appSlice';

// Utilities
import {
    mediaPortrait,
    mediaLandscapeMobile,
} from '../../../utils/misc/mediaQueries';

// Styles
import styles from './SessionStats.module.css';

/**
 * @function SessionStats
 * Describes current session information
 *
 * @example
 * return (
 *   <SessionStats gameCount={0} gameTotal={0} prizePoints={''} cardCount={0} playerCount={0} />
 * )
 */
function SessionStats({
    gameCount,
    gameTotal,
    prizePoints,
    cardCount,
    playerCount,
}: SessionStatsProps): JSX.Element {
    const dispatch = useAppDispatch();
    const playersListOpen = useAppSelector(selectPlayersListStatus);
    const isPortrait = useMediaQuery(mediaPortrait);
    const isLandscapeMobile = useMediaQuery(mediaLandscapeMobile);

    function handlePlayersClick(event: any) {
        dispatch(pushPlayersListStatus(!playersListOpen));
        event.preventDefault();
    }

    return (
        <section className={styles.container}>
            <ul className={styles.list}>
                {gameTotal > 1 ? (
                    <>
                        <li className={styles.item}>
                            Game <b>{gameCount}</b> of {gameTotal}
                        </li>
                        <li className={styles.separator}></li>
                    </>
                ) : (
                    <></>
                )}
                <li className={styles.item}>
                    Prize <b>{prizePoints}</b>
                </li>
                <li className={styles.separator}></li>
                <li className={styles.item}>
                    <b>{cardCount}</b> Cards
                </li>
                {(isPortrait || isLandscapeMobile) && (
                    <>
                        <li className={styles.separator}></li>
                        <li
                            className={`${styles.item} ${styles.players}`}
                            onClick={(e) => handlePlayersClick(e)}
                        >
                            <b>{playerCount}</b> Players
                        </li>
                    </>
                )}
            </ul>
        </section>
    );
}

/**
 * @interface SessionStatsProps
 * Describes the properties required for the SessionStats component
 *
 * @property {number} gameCount Current game number
 * @property {number} gameTotal Total number of games
 * @property {string} prizePoints Total prize points for game
 * @property {number} cardCount Number of cards for game
 * @property {number} playerCount Number of players in session
 */
export interface SessionStatsProps {
    gameCount: number;
    gameTotal: number;
    prizePoints: string;
    cardCount: number;
    playerCount: number;
}

// Exports
export default SessionStats;
